import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../api/api";
import { logClientError } from "../api/errorLogger";
import MailboxTable from "../components/MailboxTable";
import MailboxForm from "../components/MailboxForm";

export default function Mailboxes() {
    const location = useLocation();
    const [mailboxes, setMailboxes] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [selectedMailbox, setSelectedMailbox] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const fetchMailboxes = useCallback(async () => {
        const data = await apiFetch("/mailboxes");
        setMailboxes(data);
    }, []);

    const fetchTenants = useCallback(async () => {
        const data = await apiFetch("/tenants");
        setTenants(data);
    }, []);

    const loadPageData = useCallback(async () => {
        setError("");
        setIsLoading(true);

        try {
            await Promise.all([fetchMailboxes(), fetchTenants()]);
        } catch (err) {
            setError(err.message);
            logClientError(err, { page: "Mailboxes", action: "loadPageData" });
        } finally {
            setIsLoading(false);
        }
    }, [fetchMailboxes, fetchTenants]);

    useEffect(() => {
        loadPageData();
    }, [loadPageData]);

    useEffect(() => {
        if (location.state?.action === "add") {
            setSelectedMailbox(null);
            setShowForm(true);
        }
    }, [location.state]);

    const handleCreateOrUpdate = async (data) => {
        setError("");

        try {
            if (selectedMailbox) {
                await apiFetch(`/mailboxes/${selectedMailbox.id}`, {
                    method: "PATCH",
                    body: JSON.stringify(data),
                });
            } else {
                await apiFetch("/mailboxes", {
                    method: "POST",
                    body: JSON.stringify(data),
                });
            }

            setShowForm(false);
            setSelectedMailbox(null);
            await fetchMailboxes();
        } catch (err) {
            setError(err.message);
            logClientError(err, { page: "Mailboxes", action: "createOrUpdateMailbox" });
        }
    };

    const handleDelete = async (id) => {
        setError("");

        try {
            await apiFetch(`/mailboxes/${id}`, {
                method: "DELETE",
            });

            await fetchMailboxes();
        } catch (err) {
            setError(err.message);
            logClientError(err, { page: "Mailboxes", action: "deleteMailbox", mailboxId: id });
        }
    };

    const handleSendEmail = async (id) => {
        const confirmSend = window.confirm("Send email notification?");
        if (!confirmSend) return;
        setError("");

        try {
            await apiFetch(`/mailboxes/${id}/send-email`, {
                method: "POST",
            });

            await fetchMailboxes();
        } catch (err) {
            setError(err.message);
            logClientError(err, { page: "Mailboxes", action: "sendMailboxEmail", mailboxId: id });
        }
    };
    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <p className="admin-page-eyebrow">Mailbox Manager</p>
                    <h2>Mailboxes</h2>
                </div>

                <button
                    className="admin-button admin-button-primary"
                    onClick={() => {
                        setSelectedMailbox(null);
                        setShowForm(true);
                    }}
                >
                    Add Mailbox
                </button>
            </div>

            {error && <p className="admin-empty-state admin-error-state">{error}</p>}

            {isLoading ? (
                <p className="admin-empty-state">Loading mailboxes...</p>
            ) : (
                <MailboxTable
                    mailboxes={
                        location.state?.filter === "withMail"
                            ? mailboxes.filter((mailbox) => mailbox.hasMail)
                            : mailboxes
                    }
                    onEdit={(mb) => {
                        setSelectedMailbox(mb);
                        setShowForm(true);
                    }}
                    onSendEmail={handleSendEmail}
                    onDelete={handleDelete}
                />
            )}

            {showForm && (
                <div className="admin-form-panel">
                    <MailboxForm
                        mailbox={selectedMailbox}
                        tenants={tenants}
                        onSubmit={handleCreateOrUpdate}
                        onCancel={() => {
                            setShowForm(false);
                            setSelectedMailbox(null);
                        }}
                    />
                </div>
            )}
        </div>
    );
}
